/**
 * DigitalPersona WebSDK wrapper.
 *
 * Requires:
 *   1. DigitalPersona Lite Client running on the machine (wss://127.0.0.1:52181)
 *   2. WebSdk.client.min.js + fingerprint.sdk.min.js loaded (see index.html)
 *
 * The WebSDK exposes a global `Fingerprint` namespace:
 *   - new Fingerprint.WebApi()
 *   - reader.startAcquisition(sampleFormat, deviceUid)
 *   - reader.stopAcquisition()
 *   - events: 'DeviceConnected', 'DeviceDisconnected', 'SamplesAcquired',
 *             'QualityReported', 'AcquisitionStarted', 'AcquisitionStopped',
 *             'ErrorOccurred'
 */

const SAMPLE_FORMAT_INTERMEDIATE = 2; // Fingerprint.SampleFormat.Intermediate (ISO 19794-2 FMR template)
const SAMPLE_COOLDOWN_MS = 1500;      // ignore back-to-back samples from a single finger placement

class FingerprintService {
  constructor() {
    this.reader = null;
    this.listeners = { sample: [], quality: [], error: [], deviceChange: [] };
    this._lastSampleAt = 0;
  }

  isSdkLoaded() {
    return typeof window !== 'undefined' && !!window.Fingerprint;
  }

  /** Dynamically resolve the sample format from the loaded SDK if possible. */
  _sampleFormat() {
    const fp = typeof window !== 'undefined' ? window.Fingerprint : null;
    const enumVal = fp?.SampleFormat?.Intermediate;
    return typeof enumVal === 'number' ? enumVal : SAMPLE_FORMAT_INTERMEDIATE;
  }

  async init() {
    if (!this.isSdkLoaded()) {
      throw new Error('WebSDK no cargado. Verifica que Fingerprint.WebApi esté disponible.');
    }
    if (this.reader) return this.reader;
    this.reader = new window.Fingerprint.WebApi();

    this.reader.onDeviceConnected = (e) => this._emit('deviceChange', { connected: true, device: e });
    this.reader.onDeviceDisconnected = (e) => this._emit('deviceChange', { connected: false, device: e });
    this.reader.onCommunicationFailed = (e) => this._emit('error', { message: 'Lite Client no responde', detail: e });
    this.reader.onSamplesAcquired = (s) => {
      // De-bounce: DigitalPersona emits SamplesAcquired continuously while the
      // finger is on the sensor. A single placement can fire the event 2-3
      // times in <200ms. We enforce a cooldown so each finger placement counts
      // as exactly one sample from the app's point of view.
      const now = Date.now();
      if (now - this._lastSampleAt < SAMPLE_COOLDOWN_MS) return;
      this._lastSampleAt = now;

      // `s.samples` may be:
      //  - a JSON string of an array (older SDK builds), OR
      //  - an already-parsed array (current builds — completed.Samples in
      //    the WebSDK is decoded from base64+JSON.parse before dispatch).
      // Each element may itself be:
      //  - a plain base64 string, OR
      //  - an object like `{ Format, Version, Header, Data: "<b64>" }`
      //    where `Data` is the actual template payload we want to send to
      //    the backend.
      let templates = s.samples;
      if (typeof templates === 'string') {
        try { templates = JSON.parse(templates); } catch (_) { templates = [templates]; }
      }
      if (!Array.isArray(templates)) templates = [templates];
      const raw = templates[0];
      const extract = (item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          // DigitalPersona BioSample shape
          if (typeof item.Data === 'string') return item.Data;
          if (typeof item.data === 'string') return item.data;
        }
        return null;
      };
      const template_b64 = extract(raw);
      if (!template_b64) {
        this._emit('error', {
          message: 'Muestra sin plantilla base64 (formato inesperado del WebSDK).',
          detail: raw,
        });
        return;
      }
      this._emit('sample', { raw: s.samples, template_b64 });
    };
    this.reader.onQualityReported = (q) => this._emit('quality', { value: q.quality });
    this.reader.onErrorOccurred = (err) => this._emit('error', { message: 'Error de captura', detail: err });
    return this.reader;
  }

  async listDevices() {
    await this.init();
    const list = await this.reader.enumerateDevices();
    return list || [];
  }

  async start(deviceUid = null) {
    await this.init();
    this._lastSampleAt = 0; // reset cooldown for a fresh session
    return this.reader.startAcquisition(this._sampleFormat(), deviceUid || undefined);
  }

  async stop() {
    if (!this.reader) return;
    try { await this.reader.stopAcquisition(); } catch (_) { /* ignore */ }
  }

  on(event, cb) {
    if (!this.listeners[event]) return () => {};
    this.listeners[event].push(cb);
    return () => {
      this.listeners[event] = this.listeners[event].filter((f) => f !== cb);
    };
  }

  _emit(event, data) {
    (this.listeners[event] || []).forEach((cb) => {
      try { cb(data); } catch (_) { /* ignore */ }
    });
  }
}

const service = new FingerprintService();
export default service;

/**
 * Diagnostic helper: reports SDK + Lite Client + device availability.
 * Returns:
 *   { sdk_loaded, websdk_channel_ok, lite_client_reachable, devices, deviceCount, error }
 *
 * Intended for use in a "Diagnóstico" panel so ops can see exactly which layer
 * is failing when fingerprint capture doesn't work on a customer machine.
 */
export async function runDiagnostics() {
  const out = {
    sdk_loaded: false,
    websdk_channel_ok: false,
    lite_client_reachable: false,
    devices: [],
    deviceCount: 0,
    error: null,
  };
  try {
    out.sdk_loaded =
      typeof window !== 'undefined' &&
      !!window.Fingerprint &&
      typeof window.Fingerprint.WebApi === 'function';
    if (!out.sdk_loaded) {
      out.error = 'WebSDK no está cargado en la página.';
      return out;
    }
    // Instantiating WebApi opens the wss channel to Lite Client. If Lite Client
    // isn't running the enumerateDevices() call will reject with an error.
    const reader = new window.Fingerprint.WebApi();
    out.websdk_channel_ok = true;

    const devices = await Promise.race([
      reader.enumerateDevices(),
      new Promise((_, rj) =>
        setTimeout(() => rj(new Error('Timeout esperando al Lite Client (wss://127.0.0.1:52181)')), 4000),
      ),
    ]);
    out.lite_client_reachable = true;
    out.devices = Array.isArray(devices) ? devices : [];
    out.deviceCount = out.devices.length;
  } catch (err) {
    out.error =
      err?.message ||
      'No se pudo conectar con el Lite Client. Verifica que "HID Authentication Device Client" esté corriendo.';
  }
  return out;
}

/**
 * Byte-similarity comparison over decoded template bytes.
 *
 * DigitalPersona ISO 19794-2 templates share a constant header (~30 bytes:
 * format identifier, version, capture size, resolution, etc). Comparing raw
 * bytes across templates therefore artificially inflates the score toward the
 * common header. We SKIP the first `HEADER_SKIP_BYTES` and then run an n-gram
 * overlap over the remaining minutiae bytes.
 *
 * This is still a lightweight approximation of a real minutiae matcher, but
 * combined with multi-template enrollment (3 captures) it produces reliable
 * scores in the 0.15-0.60 range for the same finger and <0.05 for different
 * fingers.
 */
const HEADER_SKIP_BYTES = 30;
const NGRAM = 4;

export function compareTemplates(a, b) {
  if (!a || !b) return 0;
  try {
    const A = atob(a).slice(HEADER_SKIP_BYTES);
    const B = atob(b).slice(HEADER_SKIP_BYTES);
    if (A.length < NGRAM + 1 || B.length < NGRAM + 1) return 0;
    const setA = new Set();
    for (let i = 0; i < A.length - NGRAM; i++) setA.add(A.substr(i, NGRAM));
    let hit = 0;
    let total = 0;
    for (let i = 0; i < B.length - NGRAM; i++) {
      total++;
      if (setA.has(B.substr(i, NGRAM))) hit++;
    }
    return total > 0 ? hit / total : 0;
  } catch (_) {
    return 0;
  }
}

/**
 * 1:N match — compares `sampleTemplate` against every enrolled user's list of
 * templates and returns the best match (or null if under threshold).
 *
 * Each enrolment `e` may expose `templates_b64` (array, new format) or
 * `template_b64` (single string, legacy). We use whatever is available.
 */
export function findBestMatch(sampleTemplate, enrolledList, threshold = 0.15) {
  let best = null;
  let bestScore = 0;
  const allScores = [];
  for (const e of enrolledList) {
    const templates = Array.isArray(e.templates_b64) && e.templates_b64.length > 0
      ? e.templates_b64
      : (e.template_b64 ? [e.template_b64] : []);
    let userScore = 0;
    for (const t of templates) {
      const s = compareTemplates(sampleTemplate, t);
      if (s > userScore) userScore = s;
    }
    allScores.push({ user_id: e.user_id, user_name: e.user_name, score: userScore });
    if (userScore > bestScore) { bestScore = userScore; best = e; }
  }
  const debug = { threshold, bestScore, allScores };
  if (!best || bestScore < threshold) return { match: null, debug };
  return { match: { ...best, score: bestScore }, debug };
}
